import { createServerFn } from "@tanstack/react-start"
import { getRequestHeader } from "@tanstack/react-start/server"
import { env } from "cloudflare:workers"
import { Resend } from "resend"
import { z } from "zod"

import { validateTurnstile } from "@/cloudflare/turnstile"
import { logger } from "@/core/logger.server"
import { parseFormData } from "@/form"

export const unsubscribeFormSchema = z.object({
  email: z.email(),
  "cf-turnstile-response": z.string().optional(),
})

/**
 * Self-service unsubscribe: no login, no extra personal information beyond
 * the email itself, and takes effect immediately (well inside the Spam Act
 * 2003's 5-business-day limit). See newsletter.server.ts for why this link
 * is unsigned. Turnstile is not "extra personal information" (it's bot
 * detection, same check the subscribe form uses), so it guards against a
 * script mass-unsubscribing addresses without breaking either requirement.
 */
export const unsubscribeFormAction = createServerFn({ method: "POST" })
  .validator((data: FormData) => parseFormData(unsubscribeFormSchema, data))
  .handler(async ({ data }) => {
    const { email, "cf-turnstile-response": turnstileResponse } = data

    const cloudflareTurnstileSecretKey = env.CLOUDFLARE_TURNSTILE_SECRET_KEY
    if (cloudflareTurnstileSecretKey) {
      const ip = getRequestHeader("CF-Connecting-IP")
      const isHuman = await validateTurnstile({
        secret: cloudflareTurnstileSecretKey,
        token: turnstileResponse,
        ip,
      })

      if (!isHuman) {
        logger.warn("newsletter.unsubscribe.turnstile_failed")
        throw new Error("Human verification failed")
      }
    }

    if (!env.RESEND_API_KEY) {
      logger.error("newsletter.unsubscribe.not_configured")
      throw new Error("Newsletter is not configured")
    }

    const resend = new Resend(env.RESEND_API_KEY)
    const { error } = await resend.contacts.update({ email, unsubscribed: true })

    if (error) {
      // `error.name` is Resend's structured error code (see the `resend`
      // package's `ErrorResponse` type), not a free-text message - matching
      // on it instead of `error.message` substrings avoids breaking if
      // Resend rewords its messages.
      const isMissingContact = error.name === "not_found"
      if (!isMissingContact) {
        logger.error("newsletter.unsubscribe.resend_failed", { error })
        throw new Error("Failed to unsubscribe. Please try again.")
      }
      // Contact doesn't exist (already unsubscribed, never subscribed, or a
      // typo) - report success either way so this page can't be used to
      // probe whether an address is a subscriber.
    }

    return {
      success: "You're unsubscribed. You will not receive any more newsletter emails.",
    }
  })
